import type { AxiosInstance } from 'axios';
import type { Ref } from 'vue';
import { P2Role, type UserWithPermissions } from '@centric-os/types';
import CDLID from '@compassdigital/id';
import { useSplitIoStore } from '@centric-os/stores/splitio/use-split-io';

let instance: HeapAnalytics;

declare global {
  interface Window {
    heap: any;
    heapReadyCb: Array<{ name: string; fn: () => void }>;
    dtrum?: {
      identifyUser(userTag: string): void;
      enterAction(actionName: string): number;
      leaveAction(actionId: number): void;
    };
  }
}

interface TrackEvents {
  aiDescriptionGenerated: ({
    itemLabel,
    menuName,
    brandName,
    LocalMenuGroupName,
    description,
  }: {
    itemLabel: string;
    menuName: string;
    brandName: string;
    LocalMenuGroupName: string;
    description: string;
  }) => void;
  aiImageGenerated: ({
    itemLabel,
    menuName,
    brandName,
    LocalMenuGroupName,
  }: {
    itemLabel: string;
    menuName: string;
    brandName: string;
    LocalMenuGroupName: string;
  }) => void;
  aiImageUsed: ({
    itemLabel,
    menuName,
    brandName,
    LocalMenuGroupName,
  }: {
    itemLabel: string;
    menuName: string;
    brandName: string;
    LocalMenuGroupName: string;
  }) => void;
}

enum EVENTS {
  AI_DESCRIPTION_GENERATED = 'ai_description_generated',
  AI_IMAGE_GENERATED = 'ai_image_generated',
  AI_IMAGE_USED = 'ai_image_used',
}

class HeapAnalytics {
  private cdlUser: UserWithPermissions;
  private usedSSO: boolean;
  // @ts-expect-error FIXME: 'getRole' implicitly has 'any' type
  private getRole;
  // @ts-expect-error FIXME: 'hasRole' implicitly has 'any' type
  private hasRole;
  private http: AxiosInstance;
  public aiImageGenerated = false; // used to track an event when an AI generated image is used for an item

  async init(
    cdlUser: Ref<UserWithPermissions>,
    usedSSO: Ref<boolean>,
    // @ts-expect-error FIXME: Parameter 'getRole' implicitly has 'any' type
    getRole,
    // @ts-expect-error FIXME: Parameter 'hasRole' implicitly has 'any' type
    hasRole,
    http: AxiosInstance,
  ): Promise<void> {
    this.http = http;
    if (!window.heap?.loaded) {
      window.heap?.load(import.meta.env.VITE_HEAP_API_KEY);
    }
    this.cdlUser = cdlUser.value;
    this.usedSSO = usedSSO.value;
    this.getRole = getRole;
    this.hasRole = hasRole;

    const userProperties: Record<string, any> = {
      version: import.meta.env.VITE_APP_VERSION,
    };

    if (this.cdlUser) {
      if (this.cdlUser.email) {
        window.heap?.identify(this.cdlUser.email);
      }

      // Dynatrace RUM user tag (dt.rum.user_tag): use the canonical Centric user id
      // rather than email, so the tag is populated even for SSO users without an
      // email and matches the backend user id used in tracing.
      if (this.cdlUser.id) {
        window.dtrum?.identifyUser(this.cdlUser.id);
      }

      if (this.cdlUser.name?.first && this.cdlUser.name?.last) {
        userProperties.fullName = `${this.cdlUser.name.first} ${this.cdlUser.name.last}`;
      }

      if (this.cdlUser.date?.created) {
        userProperties.creationDate = this.cdlUser.date.created;
      }

      const sites = await this.getSiteNames();
      userProperties.site = sites;
      userProperties.usedSSO = this.usedSSO;
      userProperties.role = this.getRole;

      const splitStore = useSplitIoStore();

      if (!splitStore.initialized) {
        await splitStore.fetchFeatureFlags();
      }

      Object.entries(splitStore.featureFlags).forEach(([key, value]) => {
        if (value) userProperties[`Split.${key}`] = 'on';
      });
    }

    window.heap?.addUserProperties(userProperties);
  }

  private async getSiteNames(): Promise<string> {
    const promises: Promise<any>[] = [];
    const userSiteIds = this.getUserSiteIds();
    if (userSiteIds === 'All') return userSiteIds;
    userSiteIds.forEach((siteId) => {
      promises.push(this.http.get(`/location/group/${siteId}?include_estimated_wait_time=false`));
    });
    const sitesData = await Promise.all(promises);
    const siteNames = sitesData.map(({ data: userSite }) => userSite.name || 'None').join(',');
    return siteNames;
  }

  private getUserSiteIds(): string[] | 'All' {
    if (this.isAdmin || this.hasRole(P2Role.SYS_ADMIN)) return 'All';
    return this.getPermissions
      .filter((scope) => {
        return (
          scope.includes(P2Role.SITE_OPERATOR) ||
          scope.includes(P2Role.MENU_USER) ||
          scope.includes(P2Role.DC_TEAM) ||
          scope.includes(P2Role.RUNNER)
        );
      })
      .map((scopeId) => {
        return scopeId.split(':')[2];
      })
      .filter((scopeId) => {
        return CDLID.contains(scopeId, { type: 'group' });
      });
  }

  get track(): TrackEvents {
    const aiDescriptionGenerated = ({
      itemLabel,
      menuName,
      brandName,
      LocalMenuGroupName,
      description,
    }: {
      itemLabel: string;
      menuName: string;
      brandName: string;
      LocalMenuGroupName: string;
      description: string;
    }) => {
      window.heap.track(EVENTS.AI_DESCRIPTION_GENERATED, {
        item: itemLabel,
        menu: menuName,
        brand: brandName,
        local_menu_group: LocalMenuGroupName,
        description,
      });
    };

    const aiImageGenerated = ({
      itemLabel,
      menuName,
      brandName,
      LocalMenuGroupName,
    }: {
      itemLabel: string;
      menuName: string;
      brandName: string;
      LocalMenuGroupName: string;
    }) => {
      this.aiImageGenerated = true;
      window.heap.track(EVENTS.AI_IMAGE_GENERATED, {
        item: itemLabel,
        menu: menuName,
        brand: brandName,
        local_menu_group: LocalMenuGroupName,
      });
    };

    const aiImageUsed = ({
      itemLabel,
      menuName,
      brandName,
      LocalMenuGroupName,
    }: {
      itemLabel: string;
      menuName: string;
      brandName: string;
      LocalMenuGroupName: string;
    }) => {
      this.aiImageGenerated = false;
      window.heap.track(EVENTS.AI_IMAGE_USED, {
        item: itemLabel,
        menu: menuName,
        brand: brandName,
        local_menu_group: LocalMenuGroupName,
      });
    };

    return {
      aiDescriptionGenerated,
      aiImageGenerated,
      aiImageUsed,
    };
  }

  private get getPermissions() {
    return this.cdlUser.permissions.scopes;
  }

  private get isAdmin() {
    return this.hasRole(P2Role.ADMIN);
  }
}

export function heap(): HeapAnalytics {
  if (!instance) {
    instance = new HeapAnalytics();
  }
  return instance;
}
